<script setup lang="ts">
import { computed } from "vue";

const props = defineProps<{
	label: string;
	icon: string;
	value?: any;
	unit?: string;
	isLoading?: boolean;
	type?: "text" | "date" | "factor" | "lead-days";
	skeletonWidth?: number;
	color?: string;
}>();

const emits = defineEmits(["day:plus", "day:minus"]);

const formattedValue = computed(() => {
	if (props.isLoading || !props.value) return "";
	if (typeof props.value === "number") {
		return props.type === "factor"
			? props.value.toFixed(2)
			: props.value.toFixed(0);
	}
	return props.value;
});
</script>

<template>
	<div class="stat-label">
		<v-icon :icon="icon" color="primary" size="small" />
		<span class="ml-2">{{ label }}</span>
	</div>
	<div class="stat-value">
		<template v-if="!isLoading">
			<template v-if="type === 'text' || !type">
				<span class="text-h6">{{ formattedValue }}</span>
				<span v-if="unit" class="text-caption text-medium-emphasis ml-1">
					{{ unit }}
				</span>
			</template>

			<v-chip
				v-else-if="type === 'factor'"
				density="compact"
				class="font-weight-bold"
				size="large"
				label
				:color="color"
			>
				{{ formattedValue }}
			</v-chip>

			<div v-else-if="type === 'date'" class="d-flex justify-end align-center">
				<slot name="date-navigation-left"></slot>
				<span class="pa-1">{{ value }}</span>
				<slot name="date-navigation-right"></slot>
			</div>

			<span v-else-if="type === 'lead-days'">
				<template v-if="value >= 0">
					<span class="text-h6">{{ value }}</span>
					<span class="text-caption text-medium-emphasis ml-1">day(s)</span>
				</template>
				<template v-else>
					<span class="text-h6">{{ Math.abs(value) }}</span>
					<span class="text-caption text-medium-emphasis ml-1">day(s) ago</span>
				</template>
			</span>
		</template>

		<v-skeleton-loader
			v-else
			type="text"
			class="ml-auto py-0 mr-n4"
			:width="skeletonWidth ?? 50"
			:class="[
				type === 'factor' ? 'stat-skeleton-chip' : 'text-h6 stat-skeleton',
			]"
			:height="32"
		/>
	</div>
</template>

<style scoped>
.stat-label {
	display: flex;
	align-items: center;
	color: var(--v-medium-emphasis-color);
}

.stat-value {
	display: flex;
	text-align: right;
	justify-content: flex-end;
	align-items: center;
	font-weight: 500;
	height: 32px;
}
</style>
